import { computed, onMounted, onUnmounted, readonly, ref, watch } from 'vue';

import {
  DEFAULT_BATCH_SIZE,
  DEFAULT_RETRY_BASE_DELAY,
  DEFAULT_RETRY_MAX_DELAY,
  DEFAULT_SYNC_INTERVAL,
  HTTP_STATUS,
  RETRYABLE_STATUS_CODES,
} from '../constants.js';
import { createMutationQueue, type MutationQueue } from '../storage/mutationQueue.js';
import { createOfflineStorage, type OfflineStorage } from '../storage/offlineStorage.js';
import type { ConflictData, MutationQueueItem, SyncConfig, SyncStatus, SyncStatusType } from '../types.js';

import { useNetworkStatus } from './useNetworkStatus.js';

// Cache of instances per portal
const queueInstances = new Map<string, MutationQueue>();
const storageInstances = new Map<string, OfflineStorage>();

function getQueue(portalName: string): MutationQueue {
  if (!queueInstances.has(portalName)) {
    queueInstances.set(portalName, createMutationQueue({ portalName }));
  }
  return queueInstances.get(portalName)!;
}

function getStorage(portalName: string): OfflineStorage {
  if (!storageInstances.has(portalName)) {
    storageInstances.set(portalName, createOfflineStorage({ portalName }));
  }
  return storageInstances.get(portalName)!;
}

/**
 * Options for useOfflineSync
 */
export interface UseOfflineSyncOptions extends SyncConfig {
  /** Function to execute mutations (must be provided by consumer) */
  executeMutation: <TVariables>(item: MutationQueueItem<TVariables>) => Promise<unknown>;
  /** Callback when sync starts */
  onSyncStart?: () => void;
  /** Callback when sync completes */
  onSyncComplete?: (result: { processed: number; failed: number }) => void;
  /** Callback when sync errors */
  onSyncError?: (error: Error) => void;
  /** Callback when conflict is detected */
  onConflictDetected?: (conflict: ConflictData) => void;
  /** Callback for progress updates */
  onProgress?: (current: number, total: number) => void;
}

/**
 * Composable for managing background sync of offline mutations
 *
 * Handles:
 * - Automatic sync when going online
 * - Exponential backoff for retries
 * - Batch processing of mutations
 * - Conflict detection and handling
 * - Progress tracking
 *
 * @example
 * ```typescript
 * const { isSyncing, pendingCount, startSync, conflicts } = useOfflineSync({
 *   portalName: 'client-portal',
 *   executeMutation: async (item) => {
 *     return await api[item.method.toLowerCase()](item.resourceKey, item.variables)
 *   },
 *   onConflictDetected: (conflict) => {
 *     showConflictDialog(conflict)
 *   },
 * })
 * ```
 */
export function useOfflineSync(options: UseOfflineSyncOptions) {
  const {
    portalName,
    syncInterval = DEFAULT_SYNC_INTERVAL,
    retryBaseDelay = DEFAULT_RETRY_BASE_DELAY,
    retryMaxDelay = DEFAULT_RETRY_MAX_DELAY,
    autoSyncOnOnline = true,
    batchSize = DEFAULT_BATCH_SIZE,
    executeMutation,
    onSyncStart,
    onSyncComplete,
    onSyncError,
    onConflictDetected,
    onProgress,
  } = options;

  const queue = getQueue(portalName);
  const storage = getStorage(portalName);
  const { isOnline } = useNetworkStatus();

  // Internal state
  const status = ref<SyncStatusType>('idle');
  const pendingCount = ref(0);
  const conflictCount = ref(0);
  const failedCount = ref(0);
  const lastSyncAt = ref<number | undefined>(undefined);
  const currentError = ref<string | undefined>(undefined);
  const progress = ref(0);
  const conflicts = ref<ConflictData[]>([]);
  const isPaused = ref(false);

  let syncIntervalId: ReturnType<typeof setInterval> | null = null;
  let isSyncingInternal = false;

  /**
   * Updates counts from queue stats
   */
  async function updateCounts(): Promise<void> {
    const stats = await queue.getStats();
    pendingCount.value = stats.pending + stats.retrying;
    conflictCount.value = stats.conflict;
    failedCount.value = stats.failed;
  }

  /**
   * Calculates delay for exponential backoff
   */
  function calculateBackoffDelay(retryCount: number): number {
    const delay = retryBaseDelay * Math.pow(2, retryCount);
    return Math.min(delay, retryMaxDelay);
  }

  /**
   * Processes a single mutation
   */
  async function processMutation(item: MutationQueueItem): Promise<{ success: boolean; conflict?: ConflictData }> {
    try {
      await queue.markProcessing(item.id);

      // Execute the mutation
      const result = await executeMutation(item);

      // Success - remove from queue
      await queue.remove(item.id);

      // Update cache if result contains data
      if (result && typeof result === 'object') {
        const version = 'version' in result ? (result as Record<string, unknown>).version : undefined;
        await storage.set(item.resourceKey, result, {
          version: version as number | undefined,
        });
      }

      return { success: true };
    } catch (error) {
      // Check if it's a conflict
      if (isConflictError(error)) {
        const conflict = extractConflict(item, error);
        if (conflict) {
          conflicts.value.push(conflict);
          await queue.markConflict(item.id);
          onConflictDetected?.(conflict);
          return { success: false, conflict };
        }
      }

      // Check if it's a retryable error
      if (isRetryableError(error)) {
        const canRetry = await queue.markRetry(item.id, getErrorMessage(error));
        if (canRetry) {
          // Wait before retry (handled by next sync cycle)
          return { success: false };
        }
      }

      // Non-retryable error or max retries exceeded
      await queue.markFailed(item.id, getErrorMessage(error));

      // Rollback optimistic update if available
      if (item.rollbackData !== undefined) {
        await storage.set(item.resourceKey, item.rollbackData);
      }

      return { success: false };
    }
  }

  /**
   * Processes pending mutations in batches
   */
  async function processQueue(): Promise<{ processed: number; failed: number }> {
    let processed = 0;
    let failed = 0;

    const pending = await queue.getPending();
    const total = pending.length;

    if (total === 0) {
      return { processed: 0, failed: 0 };
    }

    // Process in batches
    for (let i = 0; i < pending.length; i += batchSize) {
      if (isPaused.value || !isOnline.value) {
        break;
      }

      const batch = pending.slice(i, i + batchSize);

      for (const item of batch) {
        if (isPaused.value || !isOnline.value) {
          break;
        }

        // Apply backoff delay for retrying items
        if (item.status === 'retrying' && item.retryCount > 0) {
          const delay = calculateBackoffDelay(item.retryCount);
          await sleep(delay);
        }

        const result = await processMutation(item);
        if (result.success) {
          processed++;
        } else {
          failed++;
        }

        // Update progress
        progress.value = Math.round(((i + processed + failed) / total) * 100);
        onProgress?.(processed + failed, total);
      }
    }

    return { processed, failed };
  }

  /**
   * Starts the sync process
   */
  async function startSync(): Promise<void> {
    if (isSyncingInternal || !isOnline.value) {
      return;
    }

    isSyncingInternal = true;
    status.value = 'syncing';
    currentError.value = undefined;
    progress.value = 0;

    onSyncStart?.();

    try {
      const result = await processQueue();

      lastSyncAt.value = Date.now();
      status.value = 'idle';

      await updateCounts();
      onSyncComplete?.(result);
    } catch (error) {
      status.value = 'error';
      currentError.value = getErrorMessage(error);
      onSyncError?.(error as Error);
    } finally {
      isSyncingInternal = false;
      progress.value = 100;
    }
  }

  /**
   * Pauses the sync process
   */
  function pauseSync(): void {
    isPaused.value = true;
    status.value = 'paused';
  }

  /**
   * Resumes the sync process
   */
  function resumeSync(): void {
    isPaused.value = false;
    if (isOnline.value) {
      void startSync();
    }
  }

  /**
   * Retries failed mutations
   */
  async function retryFailed(): Promise<void> {
    const failed = await queue.getByStatus('failed');
    for (const item of failed) {
      await queue.resetToPending(item.id);
    }
    await updateCounts();
    await startSync();
  }

  /**
   * Clears all failed mutations
   */
  async function clearFailed(): Promise<void> {
    await queue.clearFailed();
    await updateCounts();
  }

  /**
   * Resolves a conflict
   */
  async function resolveConflict(conflictId: string, resolution: 'local' | 'server', mergedData?: unknown): Promise<void> {
    const conflict = conflicts.value.find((c) => c.id === conflictId);
    if (!conflict) return;

    const resolvedData = resolution === 'local' ? conflict.localData : conflict.serverData;
    const finalData = mergedData ?? resolvedData;

    // Update local storage
    await storage.set(conflict.resourceKey, finalData, {
      version: conflict.serverVersion + 1,
    });

    // Remove mutation from queue
    await queue.remove(conflict.mutationId);

    // Remove from conflicts list
    conflicts.value = conflicts.value.filter((c) => c.id !== conflictId);

    await updateCounts();
  }

  /**
   * Dismisses a conflict (keeps server version)
   */
  async function dismissConflict(conflictId: string): Promise<void> {
    await resolveConflict(conflictId, 'server');
  }

  // Computed sync status
  const syncStatus = computed<SyncStatus>(() => ({
    status: status.value,
    pendingCount: pendingCount.value,
    conflictCount: conflictCount.value,
    failedCount: failedCount.value,
    lastSyncAt: lastSyncAt.value,
    error: currentError.value,
    progress: progress.value,
  }));

  const isSyncing = computed(() => status.value === 'syncing');
  const hasErrors = computed(() => failedCount.value > 0 || currentError.value !== undefined);
  const hasConflicts = computed(() => conflictCount.value > 0);
  const hasPending = computed(() => pendingCount.value > 0);

  // Watch for online status changes
  watch(isOnline, (online) => {
    if (online && autoSyncOnOnline && !isPaused.value) {
      status.value = 'idle';
      void startSync();
    } else if (!online) {
      status.value = 'waiting_for_network';
    }
  });

  // Start periodic sync
  function startPeriodicSync(): void {
    if (syncIntervalId) return;

    syncIntervalId = setInterval(() => {
      if (isOnline.value && !isPaused.value && !isSyncingInternal) {
        void startSync();
      }
    }, syncInterval);
  }

  // Stop periodic sync
  function stopPeriodicSync(): void {
    if (syncIntervalId) {
      clearInterval(syncIntervalId);
      syncIntervalId = null;
    }
  }

  // Lifecycle
  onMounted(async () => {
    await updateCounts();
    if (!isOnline.value) {
      status.value = 'waiting_for_network';
    }
    startPeriodicSync();
  });

  onUnmounted(() => {
    stopPeriodicSync();
  });

  return {
    // State
    syncStatus,
    isSyncing,
    isPaused: readonly(isPaused),
    pendingCount: readonly(pendingCount),
    conflictCount: readonly(conflictCount),
    failedCount: readonly(failedCount),
    lastSyncAt: readonly(lastSyncAt),
    progress: readonly(progress),
    hasErrors,
    hasConflicts,
    hasPending,
    conflicts: readonly(conflicts),
    currentError: readonly(currentError),

    // Methods
    startSync,
    pauseSync,
    resumeSync,
    retryFailed,
    clearFailed,
    resolveConflict,
    dismissConflict,
    updateCounts,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isConflictError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  if ('response' in error) {
    const response = (error as { response?: { status?: number } }).response;
    return response?.status === HTTP_STATUS.CONFLICT;
  }
  if ('status' in error) {
    return (error as { status: number }).status === HTTP_STATUS.CONFLICT;
  }
  return false;
}

function isRetryableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  // Network errors are retryable
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Check status code
  let statusCode: number | undefined;
  if ('response' in error) {
    statusCode = (error as { response?: { status?: number } }).response?.status;
  } else if ('status' in error) {
    statusCode = (error as { status: number }).status;
  }

  if (statusCode) {
    return RETRYABLE_STATUS_CODES.includes(statusCode as (typeof RETRYABLE_STATUS_CODES)[number]);
  }

  return false;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unknown error';
}

function extractConflict(item: MutationQueueItem, error: unknown): ConflictData | null {
  if (!error || typeof error !== 'object') return null;

  let data: unknown;
  if ('response' in error) {
    data = (error as { response?: { data?: unknown } }).response?.data;
  } else if ('data' in error) {
    data = (error as { data: unknown }).data;
  }

  if (!data || typeof data !== 'object') return null;

  const conflictData = data as Record<string, unknown>;

  if ('localData' in conflictData && 'serverData' in conflictData && 'localVersion' in conflictData && 'serverVersion' in conflictData) {
    return {
      id: `conflict-${Date.now()}-${item.id}`,
      resourceKey: item.resourceKey,
      mutationId: item.id,
      localData: conflictData.localData as Record<string, unknown>,
      serverData: conflictData.serverData as Record<string, unknown>,
      localVersion: conflictData.localVersion as number,
      serverVersion: conflictData.serverVersion as number,
      detectedAt: Date.now(),
      serverTimestamp: (conflictData.serverTimestamp as string) ?? new Date().toISOString(),
      conflictingFields: detectConflictingFields(
        conflictData.localData as Record<string, unknown>,
        conflictData.serverData as Record<string, unknown>,
      ),
    };
  }

  return null;
}

function detectConflictingFields(local: Record<string, unknown>, server: Record<string, unknown>): string[] {
  const fields: string[] = [];
  const allKeys = new Set([...Object.keys(local), ...Object.keys(server)]);

  for (const key of allKeys) {
    if (JSON.stringify(local[key]) !== JSON.stringify(server[key])) {
      fields.push(key);
    }
  }

  return fields;
}
